import React, { useMemo } from 'react';
import { Control, Controller, FieldValues, Path } from 'react-hook-form';
import { FormField, Select, Spinner } from '@legalshield/adonis-ux-framework';

import { COUNTRY_OPTIONS, STATE_CODE_MAP } from '../constants';
import { useStatesByCountryCode } from '../queriesClient';
import { CountryAndState, StateOption } from '../types';
import SearchWithDropDown, { DropDownOption } from './SearchWithDropDown';

type Props<T extends FieldValues> = {
  control: Control<T>;
  name: Path<T>;
  required?: boolean;
  excludedStateCodes?: string[];
};

const LocationMapperCountryState = <T extends FieldValues>({
  control,
  name,
  required,
  excludedStateCodes,
}: Props<T>) => (
  <Controller
    control={control}
    name={name}
    render={({ field, fieldState }) => {
      const value = (field.value as CountryAndState | undefined) ?? {
        country: '',
        states: [],
      };
      return (
        <div className="rnq-field-group">
          <FormField
            id={`${String(name)}-country`}
            label="Country"
            required={required}
            status={fieldState.error ? 'invalid' : undefined}
            validationHint={fieldState.error?.message}
          >
            <Select
              placeholder="Select a country"
              placeholderValue=""
              value={value.country}
              options={COUNTRY_OPTIONS.map((c) => ({ label: c.title, value: c.value }))}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                field.onChange({ country: e.target.value, states: [] })
              }
            />
          </FormField>

          {value.country && (
            <StatesPicker
              countryCode={value.country}
              selected={value.states}
              required={required}
              excludedStateCodes={excludedStateCodes}
              onChange={(next) => field.onChange({ country: value.country, states: next })}
            />
          )}
        </div>
      );
    }}
  />
);

const StatesPicker = ({
  countryCode,
  selected,
  required,
  excludedStateCodes,
  onChange,
}: {
  countryCode: string;
  selected: StateOption[];
  required?: boolean;
  excludedStateCodes?: string[];
  onChange: (next: StateOption[]) => void;
}) => {
  const { data, isLoading, isError } = useStatesByCountryCode(countryCode);

  const excluded = useMemo(() => new Set(excludedStateCodes ?? []), [excludedStateCodes]);

  const options = useMemo<DropDownOption[]>(() => {
    if (!data) return [];
    const stateNameAliases: Record<string, string> = { Vi: 'Virgin Islands' };
    return data
      .map((s) => {
        const canonical = stateNameAliases[s.State] ?? s.State;
        const map = STATE_CODE_MAP.find((m) => m.state === canonical);
        return { title: canonical, value: map?.code ?? canonical };
      })
      .filter((opt) => !excluded.has(opt.value))
      .sort((a, b) => a.title.localeCompare(b.title));
  }, [data, excluded]);

  const selectedAsOptions = selected.map((s) => ({ title: s.title, value: s.value }));

  if (isLoading) {
    return (
      <FormField id="states-picker" label="State/Province/Territory" required={required}>
        <Spinner />
      </FormField>
    );
  }
  if (isError) {
    return (
      <FormField id="states-picker" label="State/Province/Territory" required={required}>
        <p className="rnq-error">Could not load states for {countryCode}.</p>
      </FormField>
    );
  }

  return (
    <SearchWithDropDown
      label="State/Province/Territory"
      required={required}
      options={options}
      value={selectedAsOptions}
      onChange={(next) => {
        const mapped: StateOption[] = next.map((n) => ({
          title: n.title,
          value: n.value,
          countryCode,
        }));
        onChange(mapped);
      }}
    />
  );
};

export default LocationMapperCountryState;
